import { useState, useEffect, useCallback } from "react";
import { Landmark, Plus, X, Loader2, Trash2, Edit3, ArrowUpRight, ArrowDownLeft, Link2 } from "lucide-react";
import axios from "axios";
import { API, useAuth } from "../contexts/AuthContext";
import { formatPYG, formatDate, todayAsuncion } from "../utils/format";
import { roleAllowed } from "../utils/roles";
import { toast } from "sonner";

// Fix CH (2026-09-10): "En Tesorería aparte de Pagos y Cobranzas debe estar también
// Procesos Bancarios (…) el gerente financiero debe poder vincular cada Orden de Pago y
// Orden de Cobro a un registro de emisión y recepción entre cuentas bancarias" (Sergio).
const BASE = `${API}/treasury/bancos`;
const opts = { withCredentials: true };
const EDITAN = ["Superadministrador", "Administrador", "Gerencia Financiera"];
const CUENTA_VACIA = { banco: "", nombre: "", numero: "", moneda: "PYG", titular: "", activa: true };
const inputCls = "w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-200";

function Campo({ label, children }) {
  return <div><label className="block text-xs font-bold text-gray-700 mb-1.5">{label}</label>{children}</div>;
}

function MovimientoModal({ cuentas, pendientes, onClose, onSaved }) {
  const [f, setF] = useState({ vinculo_tipo: "orden_pago", vinculo_id: "", cuenta_id: cuentas[0]?._id || "", fecha: todayAsuncion(),
    monto: "", referencia: "", contraparte_banco: "", contraparte_cuenta: "", contraparte_titular: "", notas: "" });
  const [saving, setSaving] = useState(false);
  const docs = pendientes.filter(p => p.vinculo_tipo === f.vinculo_tipo);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const elegirDoc = (id) => {
    const d = pendientes.find(p => p.vinculo_id === id);
    setF(prev => ({ ...prev, vinculo_id: id, monto: d ? String(Math.round(d.monto)) : prev.monto, contraparte_titular: d ? d.contraparte : prev.contraparte_titular }));
  };
  const guardar = async () => {
    if (!f.vinculo_id || !f.cuenta_id || !(Number(f.monto) > 0)) { toast.error("Elegí el documento, la cuenta y el monto"); return; }
    setSaving(true);
    try {
      await axios.post(`${BASE}/movimientos`, { ...f, monto: Number(f.monto) }, opts);
      toast.success("Movimiento registrado y vinculado");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al registrar el movimiento"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="banco-mov-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">Vincular movimiento bancario</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-3">
          <Campo label="Tipo">
            <select value={f.vinculo_tipo} onChange={e => setF(prev => ({ ...prev, vinculo_tipo: e.target.value, vinculo_id: "", monto: "" }))} className={inputCls} data-testid="banco-mov-tipo">
              <option value="orden_pago">Emisión — Orden de Pago</option>
              <option value="orden_cobro">Recepción — Orden de Cobro</option>
            </select>
          </Campo>
          <Campo label={`Documento (${docs.length} sin vincular)`}>
            <select value={f.vinculo_id} onChange={e => elegirDoc(e.target.value)} className={inputCls} data-testid="banco-mov-doc">
              <option value="">Elegir…</option>
              {docs.map(d => <option key={d.vinculo_id} value={d.vinculo_id}>{d.numero} · {d.contraparte || "—"} · {formatPYG(d.monto)}</option>)}
            </select>
          </Campo>
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Cuenta de la empresa">
              <select value={f.cuenta_id} onChange={e => set("cuenta_id", e.target.value)} className={inputCls} data-testid="banco-mov-cuenta">
                <option value="">Elegir…</option>
                {cuentas.filter(c => c.activa !== false).map(c => <option key={c._id} value={c._id}>{c.banco} {c.numero} ({c.moneda})</option>)}
              </select>
            </Campo>
            <Campo label="Fecha"><input type="date" value={f.fecha} onChange={e => set("fecha", e.target.value)} className={inputCls} data-testid="banco-mov-fecha" /></Campo>
            <Campo label="Monto"><input type="number" value={f.monto} onChange={e => set("monto", e.target.value)} className={inputCls} data-testid="banco-mov-monto" /></Campo>
            <Campo label="N° de transferencia"><input value={f.referencia} onChange={e => set("referencia", e.target.value)} className={inputCls} data-testid="banco-mov-ref" /></Campo>
            <Campo label="Banco contraparte"><input value={f.contraparte_banco} onChange={e => set("contraparte_banco", e.target.value)} className={inputCls} /></Campo>
            <Campo label="Cuenta contraparte"><input value={f.contraparte_cuenta} onChange={e => set("contraparte_cuenta", e.target.value)} className={inputCls} /></Campo>
          </div>
          <Campo label="Titular contraparte"><input value={f.contraparte_titular} onChange={e => set("contraparte_titular", e.target.value)} className={inputCls} /></Campo>
          <Campo label="Notas"><input value={f.notas} onChange={e => set("notas", e.target.value)} className={inputCls} /></Campo>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button onClick={guardar} disabled={saving} className="flex-1 flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="banco-mov-guardar">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Link2 size={15} />} Registrar y vincular
          </button>
        </div>
      </div>
    </div>
  );
}

function CuentaModal({ cuenta, onClose, onSaved }) {
  const [f, setF] = useState(cuenta?._id ? { ...CUENTA_VACIA, ...cuenta } : CUENTA_VACIA);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const guardar = async () => {
    if (!f.banco.trim()) { toast.error("El banco es obligatorio"); return; }
    setSaving(true);
    try {
      const body = { banco: f.banco, nombre: f.nombre, numero: f.numero, moneda: f.moneda, titular: f.titular, activa: !!f.activa };
      if (cuenta?._id) await axios.put(`${BASE}/cuentas/${cuenta._id}`, body, opts);
      else await axios.post(`${BASE}/cuentas`, body, opts);
      toast.success("Cuenta guardada");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar la cuenta"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="banco-cuenta-modal">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-base font-bold text-gray-900">{cuenta?._id ? "Editar cuenta" : "Nueva cuenta bancaria"}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="p-6 grid grid-cols-2 gap-3">
          <Campo label="Banco *"><input value={f.banco} onChange={e => set("banco", e.target.value)} className={inputCls} data-testid="banco-cuenta-banco" /></Campo>
          <Campo label="N° de cuenta"><input value={f.numero} onChange={e => set("numero", e.target.value)} className={inputCls} data-testid="banco-cuenta-numero" /></Campo>
          <Campo label="Nombre / alias"><input value={f.nombre} onChange={e => set("nombre", e.target.value)} className={inputCls} /></Campo>
          <Campo label="Moneda">
            <select value={f.moneda} onChange={e => set("moneda", e.target.value)} className={inputCls}><option value="PYG">PYG</option><option value="USD">USD</option></select>
          </Campo>
          <div className="col-span-2"><Campo label="Titular"><input value={f.titular} onChange={e => set("titular", e.target.value)} className={inputCls} /></Campo></div>
          <label className="col-span-2 flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={!!f.activa} onChange={e => set("activa", e.target.checked)} /> Activa</label>
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          <button onClick={guardar} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="banco-cuenta-guardar">
            {saving ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TesoreriaBancos() {
  const { user } = useAuth();
  const canEdit = roleAllowed(user?.role, EDITAN);
  const [tab, setTab] = useState("movimientos");
  const [movs, setMovs] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [showMov, setShowMov] = useState(false);
  const [editCuenta, setEditCuenta] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [m, c, p] = await Promise.all([
        axios.get(`${BASE}/movimientos`, { ...opts, params: { desde: desde || undefined, hasta: hasta || undefined } }),
        axios.get(`${BASE}/cuentas`, opts),
        axios.get(`${BASE}/pendientes`, opts),
      ]);
      setMovs(m.data || []); setCuentas(c.data || []); setPendientes(p.data || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cargar Procesos Bancarios"); }
    finally { setLoading(false); }
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const emitido = movs.filter(m => m.tipo === "emision").reduce((a, m) => a + (m.monto || 0), 0);
  const recibido = movs.filter(m => m.tipo === "recepcion").reduce((a, m) => a + (m.monto || 0), 0);

  const borrarMov = async (m) => {
    if (!window.confirm(`¿Eliminar el movimiento de ${m.vinculo_numero}? El documento queda sin vincular.`)) return;
    try { await axios.delete(`${BASE}/movimientos/${m._id}`, opts); toast.success("Movimiento eliminado"); cargar(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };
  const borrarCuenta = async (c) => {
    if (!window.confirm(`¿Eliminar la cuenta ${c.banco} ${c.numero}?`)) return;
    try { await axios.delete(`${BASE}/cuentas/${c._id}`, opts); toast.success("Cuenta eliminada"); cargar(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="procesos-bancarios">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full uppercase tracking-wide">Tesorería</span>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mt-1">Procesos Bancarios</h1>
          <p className="text-sm text-gray-500 mt-0.5">Emisiones y recepciones entre cuentas bancarias, vinculadas a cada Orden de Pago y Orden de Cobro</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <button onClick={() => setEditCuenta({})} className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-xl text-sm font-semibold" data-testid="banco-nueva-cuenta-btn"><Plus size={14} /> Cuenta</button>
            <button onClick={() => (cuentas.length ? setShowMov(true) : toast.error("Primero cargá una cuenta bancaria"))} className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-2 rounded-xl text-sm font-semibold" data-testid="banco-vincular-btn"><Link2 size={14} /> Vincular movimiento</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200/60 p-5"><p className="text-xs font-semibold text-gray-500 uppercase">Emitido</p><p className="text-xl font-bold text-red-600">{formatPYG(emitido)}</p></div>
        <div className="bg-white rounded-xl border border-gray-200/60 p-5"><p className="text-xs font-semibold text-gray-500 uppercase">Recibido</p><p className="text-xl font-bold text-emerald-600">{formatPYG(recibido)}</p></div>
        <div className="bg-white rounded-xl border border-gray-200/60 p-5" data-testid="banco-sin-vincular"><p className="text-xs font-semibold text-gray-500 uppercase">Documentos sin vincular</p><p className="text-xl font-bold text-amber-600">{pendientes.length}</p></div>
      </div>

      <div className="flex gap-1 border-b border-gray-200">
        {[["movimientos", "Movimientos"], ["cuentas", "Cuentas bancarias"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-5 py-3 text-sm font-semibold border-b-2 ${tab === k ? "border-slate-800 text-slate-800" : "border-transparent text-gray-500"}`} data-testid={`banco-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "movimientos" && (
        <div className="space-y-3">
          <div className="flex gap-3 items-end">
            <div><label className="text-xs font-semibold text-gray-500 block mb-1">Desde</label><input type="date" value={desde} onChange={e => setDesde(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="banco-desde" /></div>
            <div><label className="text-xs font-semibold text-gray-500 block mb-1">Hasta</label><input type="date" value={hasta} onChange={e => setHasta(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm" data-testid="banco-hasta" /></div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-gray-100 bg-gray-50/50 text-xs text-gray-400 uppercase">
                <th className="text-left px-4 py-3">Fecha</th><th className="text-left px-4 py-3">Tipo</th><th className="text-left px-4 py-3">Documento</th>
                <th className="text-left px-4 py-3">Cuenta empresa</th><th className="text-left px-4 py-3">Contraparte</th><th className="text-left px-4 py-3">Ref.</th>
                <th className="text-right px-4 py-3">Monto</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={8} className="text-center py-10"><Loader2 className="animate-spin mx-auto text-gray-400" /></td></tr>
                  : movs.length === 0 ? <tr><td colSpan={8} className="text-center py-10 text-gray-400"><Landmark size={28} className="mx-auto mb-2 opacity-30" />Sin movimientos</td></tr>
                  : movs.map(m => (
                    <tr key={m._id} className="border-b border-gray-50" data-testid={`banco-mov-${m._id}`}>
                      <td className="px-4 py-3 whitespace-nowrap">{formatDate(m.fecha)}</td>
                      <td className="px-4 py-3">{m.tipo === "emision"
                        ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-full"><ArrowUpRight size={11} /> Emisión</span>
                        : <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full"><ArrowDownLeft size={11} /> Recepción</span>}</td>
                      <td className="px-4 py-3 font-mono text-xs">{m.vinculo_numero}</td>
                      <td className="px-4 py-3">{m.cuenta_label}</td>
                      <td className="px-4 py-3">{[m.contraparte_titular, m.contraparte_banco, m.contraparte_cuenta].filter(Boolean).join(" · ") || "—"}</td>
                      <td className="px-4 py-3 text-xs">{m.referencia || "—"}</td>
                      <td className="px-4 py-3 text-right font-bold">{formatPYG(m.monto)}</td>
                      <td className="px-4 py-3 text-right">{canEdit && <button onClick={() => borrarMov(m)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`banco-mov-borrar-${m._id}`}><Trash2 size={13} /></button>}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "cuentas" && (
        <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-gray-100 bg-gray-50/50 text-xs text-gray-400 uppercase">
              <th className="text-left px-4 py-3">Banco</th><th className="text-left px-4 py-3">N° de cuenta</th><th className="text-left px-4 py-3">Nombre</th>
              <th className="text-left px-4 py-3">Moneda</th><th className="text-left px-4 py-3">Titular</th><th className="text-left px-4 py-3">Estado</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {cuentas.length === 0 ? <tr><td colSpan={7} className="text-center py-10 text-gray-400">Sin cuentas cargadas</td></tr>
                : cuentas.map(c => (
                  <tr key={c._id} className="border-b border-gray-50" data-testid={`banco-cuenta-${c._id}`}>
                    <td className="px-4 py-3 font-semibold">{c.banco}</td><td className="px-4 py-3 font-mono text-xs">{c.numero || "—"}</td>
                    <td className="px-4 py-3">{c.nombre || "—"}</td><td className="px-4 py-3">{c.moneda}</td><td className="px-4 py-3">{c.titular || "—"}</td>
                    <td className="px-4 py-3">{c.activa !== false ? <span className="text-xs text-emerald-700">Activa</span> : <span className="text-xs text-gray-400">Inactiva</span>}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">{canEdit && (<>
                      <button onClick={() => setEditCuenta(c)} className="p-1 text-blue-600 hover:bg-blue-50 rounded mr-1"><Edit3 size={13} /></button>
                      <button onClick={() => borrarCuenta(c)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 size={13} /></button></>)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {showMov && <MovimientoModal cuentas={cuentas} pendientes={pendientes} onClose={() => setShowMov(false)} onSaved={() => { setShowMov(false); cargar(); }} />}
      {editCuenta && <CuentaModal cuenta={editCuenta} onClose={() => setEditCuenta(null)} onSaved={() => { setEditCuenta(null); cargar(); }} />}
    </div>
  );
}
